
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Generates a valid UUID v4.
 * Essential for Supabase/PostgreSQL compatibility where 'id' columns are UUID type.
 */
export const generateId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // Fallback for older environments
  }
  
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Calculates Levenshtein Distance between two strings.
 */
export const levenshteinDistance = (s1: string, s2: string): number => {
  const m = s1.length;
  const n = s2.length;
  const d: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;

  for (let j = 1; j <= n; j++) {
    for (let i = 1; i <= m; i++) {
      const substitutionCost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1, // deletion
        d[i][j - 1] + 1, // insertion
        d[i - 1][j - 1] + substitutionCost // substitution
      );
    }
  }

  return d[m][n];
};

/**
 * Determines if a user answer is "close enough" to the target answer.
 * Permitting minor spelling errors and ignoring noise words/prepositions.
 */
export const isFuzzyMatch = (userInput: string, target: string): boolean => {
  const normalize = (s: string) => {
    // 1. Lowercase and remove punctuation
    let clean = s.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " ");
    
    // 2. Remove common noise words and prepositions
    const noiseWords = /\b(the|of|in|and|a|an|at|with|section|tissue|slide|view|from|layer)\b/g;
    clean = clean.replace(noiseWords, " ");
    
    // 3. Compact whitespace
    return clean.replace(/\s+/g, " ").trim();
  };

  const nUser = normalize(userInput);
  const nTarget = normalize(target);

  // Exact match after normalization
  if (nUser === nTarget) return true;
  if (!nUser || !nTarget) return false;

  // Keyword check: if user provided the core word (e.g., target is "Liver" and user typed "Section of Liver")
  // it would already match via normalize. But if they typed "Liverr", we check distance.
  const distance = levenshteinDistance(nUser, nTarget);
  
  // Define threshold: 1 error for short words, 2 for medium, 3 for long
  // Max 20% error rate permitted
  const threshold = Math.max(1, Math.floor(nTarget.length * 0.25));
  
  return distance <= threshold;
};
